import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from "@angular/core";
import { IAMAccountJwtVO, KeycloakAccountJwtVO } from "@common/domain";
import { AccountAuthFacade } from "@youm-front/account-auth/data-access";
import { AccountJwtService } from "@youm-front/account-jwt/data-access";
import { AccountFacade } from "@youm-front/account/data-access";
import {
  IAM_ACCOUNT_CONFIG,
  IAMAccountConfig
} from "@youm-front/iam-account/data-access";
import { PasswordUpdateFacade } from "@youm-front/iam-account/feature/password-update";
import {
  KEYCLOAK_CONFIG,
  KeycloakConfig
} from "@youm-front/keycloak/data-access";
import { VERSION } from "@youm-front/utils/version";
import { VERSION as AVCORE_VERSION } from "avcore";
import { Subject } from "rxjs";
import { filter, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { runtimeEnvironment } from "../runtime-environments/runtime-environment";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  readonly version = [
    VERSION,
    AVCORE_VERSION.avcore,
    AVCORE_VERSION.mediasoupClient
  ].join(" - ");
  readonly consoleUrl = new URL("/console", runtimeEnvironment.appURL);
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly accountAuthFacade: AccountAuthFacade,
    private readonly accountFacade: AccountFacade,
    private readonly accountJwtService: AccountJwtService,
    @Inject(IAM_ACCOUNT_CONFIG)
    private readonly iamAccountConfig: null | IAMAccountConfig,
    private readonly passwordUpdateFacade: PasswordUpdateFacade,
    @Inject(KEYCLOAK_CONFIG)
    private readonly keycloakConfig: null | KeycloakConfig
  ) {}

  ngOnInit() {
    // Should be within the effects
    this.accountJwtService.anyAccountJwtVO$
      .pipe(
        // Wait until has token.
        // Otherwise, during the keycloak sign in,
        // it would have keycloak token but the account token would be empty,
        // and it would sign out from the keycloak
        filter((_) => _ instanceof KeycloakAccountJwtVO),
        switchMap(() =>
          this.accountJwtService.accountJwt$.pipe(
            // Sign out from keycloak if the account was signed out.
            // It would allow user to re-login using another keycloak account,
            // otherwise the user would be stuck with a logged account
            filter((_) => !_),
            // AccountAuthModule can be used only within this application.
            // Other applications will make a redirection to this app to handle sign out logic.
            tap(() => this.accountAuthFacade.keycloakSignOut())
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get appUrl() {
    return runtimeEnvironment.appURL;
  }

  get authorized$() {
    return this.accountFacade.authorized$;
  }

  get iamAuth() {
    return !!this.iamAccountConfig;
  }

  get isRoot$() {
    return this.accountJwtService.anyAccountJwtVO$.pipe(
      map(
        (_) =>
          _ instanceof KeycloakAccountJwtVO ||
          (_ instanceof IAMAccountJwtVO && _.value.isRoot)
      )
    );
  }

  get isIAMAccount$() {
    return this.accountJwtService.anyAccountJwtVO$.pipe(
      map((_) => _ instanceof IAMAccountJwtVO)
    );
  }

  get isIdentityAccount$() {
    return this.accountJwtService.identityAccountJwtVO$.pipe(map((_) => !!_));
  }

  get keycloakAuth() {
    return !!this.keycloakConfig;
  }

  get notAuthorized$() {
    return this.accountFacade.authorized$.pipe(map((_) => !_));
  }

  get name$() {
    return this.accountJwtService.anyAccountJwtVO$.pipe(
      map((_) => _?.value.identity)
    );
  }

  onIAMAccountChangePassword() {
    this.passwordUpdateFacade.passwordUpdate();
  }

  onKeycloakSignIn() {
    this.accountAuthFacade.keycloakSignIn();
  }

  onSignOut() {
    this.accountFacade.signOut();
  }
}
